import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Upload,
  Loader2,
  Building2,
  FileDown,
  MapPin,
  Globe,
  Navigation,
  Settings2,
  Palette,
  Bell,
  Database,
  Trash2,
  AlertTriangle,
  Info,
  RefreshCw,
  Users,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Copy,
  KeyRound,
} from "lucide-react";
import { triggerGpsReport } from "@/utils/gps-report.functions";
import { deleteCompanyAccount } from "@/utils/account-deletion.functions";
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, loadLanguage, type SupportedLanguage } from "@/i18n";
import { useAddressValidation } from "@/hooks/useAddressValidation";
import { AddressValidationDialog } from "@/components/address/AddressValidationDialog";
import { EstimatePdfTemplateEditor } from "@/components/settings/EstimatePdfTemplateEditor";
import { AlertSettingsEditor } from "@/components/settings/AlertSettingsEditor";
import { WhatsAppSettingsEditor } from "@/components/settings/WhatsAppSettingsEditor";
import { CHART_THEMES, setStoredChartTheme, type ChartThemeId } from "@/lib/chart-theme";

import { ChartThemePickerCompact } from "@/components/settings/ChartThemePickerCompact";
import { CollapsibleCard } from "@/components/settings/CollapsibleCard";

interface GpsReport {
  id: string;
  period_start: string;
  period_end: string;
  storage_path: string;
  total_attempts: number;
  out_of_radius_count: number;
  generated_at: string;
}

interface ImportSourceUser {
  id: string;
  full_name: string | null;
  email: string | null;
}

interface ImportReport {
  counts: Record<string, number>;
  skipped: Array<{ table: string; sourceId: unknown; reason: string }>;
  warnings: unknown[];
  renumbered?: Array<{ table: string; column: string; from: string; to: string }>;
  configSkipped?: Array<{ table: string; column: string; value: string }>;
  dedupedCount?: number;
  filesToUpload?: number;
  bytesToUpload?: number;
  filesMissing?: number;
  usersToCreate?: number;
  usersToMap?: number;
  usersToMove?: number;
  usersNotMappable?: number;
}

type ImportChoiceMode = "map" | "create" | "move" | "none";

interface ImportChoice {
  mode: ImportChoiceMode;
  targetId?: string;
}

interface ImportClassification {
  sourceId: string;
  email: string | null;
  status: "mappable" | "creatable" | "elsewhere" | "skip";
  suggestedTargetId?: string;
  existingUserId?: string;
}

interface CreatedLogin {
  email: string;
  tempPassword: string;
}

interface ImportDryRun {
  report: ImportReport;
  targetCounts: Record<string, number>;
  sourceUsers: ImportSourceUser[];
  classification?: ImportClassification[];
  mode?: "restore" | "transfer";
}

interface CompanyMember {
  id: string;
  full_name: string | null;
  email: string | null;
}

function describeUnknown(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

export const Route = createFileRoute("/_app/settings")({
  component: SettingsPage,
});

const CURRENCIES = ["USD", "EUR", "GBP", "BRL", "CAD", "AUD", "MXN"];
const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Lisbon",
  "Europe/Madrid",
];
const PDF_BRANDING_DOCUMENT_TYPES = ["estimate", "invoice", "contract", "gps_report"] as const;

function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { company, hasRole, refresh, user } = useAuth();
  const isAdmin = hasRole("admin");
  const [name, setName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [currency, setCurrency] = useState("USD");
  const [taxPercent, setTaxPercent] = useState<number>(0);
  const [ein, setEin] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Branding / contact info shown on PDF headers and footers
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setStateField] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [phone, setPhone] = useState("");
  const [emailContact, setEmailContact] = useState("");
  const [website, setWebsite] = useState("");
  const [pdfPrimaryColor, setPdfPrimaryColor] = useState("#222933");
  const [pdfFooterNote, setPdfFooterNote] = useState("");
  const [chartTheme, setChartThemeState] = useState<ChartThemeId>("default");
  const [savingChartTheme, setSavingChartTheme] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [savingPdfBranding, setSavingPdfBranding] = useState(false);
  const [reports, setReports] = useState<GpsReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [gpsRadius, setGpsRadius] = useState<number>(100);
  const [requireGps, setRequireGps] = useState<boolean>(false);
  const [savingGps, setSavingGps] = useState(false);
  const [generatingBackup, setGeneratingBackup] = useState(false);
  // ---- Import wizard state ----
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importSourceUsers, setImportSourceUsers] = useState<ImportSourceUser[]>([]);
  const [importChoices, setImportChoices] = useState<Record<string, ImportChoice>>({});
  const [importClassification, setImportClassification] = useState<ImportClassification[]>([]);
  const [createdLogins, setCreatedLogins] = useState<CreatedLogin[]>([]);
  const [importDryRun, setImportDryRun] = useState<ImportDryRun | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importDupConfirmed, setImportDupConfirmed] = useState(false);
  const [importMembers, setImportMembers] = useState<CompanyMember[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAccount, setDeletingAccount] = useState(false);
  const navigate = useNavigate();

  const {
    validate: validateAddr,
    validating: validatingAddr,
    dialogProps: addrDialogProps,
  } = useAddressValidation();

  const currentLang = (i18n.resolvedLanguage ?? i18n.language ?? "pt") as SupportedLanguage;
  const [pendingLang, setPendingLang] = useState<SupportedLanguage>(currentLang);
  const [savingLang, setSavingLang] = useState(false);

  // Keep the pending selection in sync when the active language changes
  // outside of this card (e.g. on first hydration).
  useEffect(() => {
    setPendingLang(currentLang);
  }, [currentLang]);

  useEffect(() => {
    if (!company) return;

    let cancelled = false;
    const c = company as unknown as Record<string, string | null>;

    setName(company.name);
    setOwnerName((company as any).owner_name ?? "");
    setTimezone(company.timezone);
    setCurrency(company.currency);
    setTaxPercent(Number(company.tax_percent) || 0);
    setEin(c.ein ?? "");
    setLogoUrl(company.logo_url);
    setGpsRadius(Number(company.gps_default_radius_meters) || 100);
    setRequireGps(Boolean(company.require_job_site_gps));
    setAddressLine1(c.address_line1 ?? "");
    setAddressLine2(c.address_line2 ?? "");
    setCity(c.city ?? "");
    setStateField(c.state ?? "");
    setPostalCode(c.postal_code ?? "");
    setCountry(c.country ?? "");
    setPhone(c.phone ?? "");
    setEmailContact(c.email ?? "");
    setWebsite(c.website ?? "");
    setPdfPrimaryColor((c.pdf_primary_color as string | null) ?? "#222933");
    setPdfFooterNote((c.pdf_footer_note as string | null) ?? "");
    const ct = (c.chart_theme as string | null) ?? "default";
    setChartThemeState((CHART_THEMES as string[]).includes(ct) ? (ct as ChartThemeId) : "default");

    void (async () => {
      const { data, error } = await supabase
        .from("pdf_templates" as never)
        .select("primary_color, footer_text")
        .eq("company_id", company.id)
        .eq("document_type", "estimate")
        .maybeSingle();

      if (cancelled || error || !data) return;

      const template = data as Record<string, unknown>;
      const templateColor = (template.primary_color as string | null)?.trim();
      const templateFooter = (template.footer_text as string | null) ?? "";

      if (templateColor) setPdfPrimaryColor(templateColor);
      setPdfFooterNote(templateFooter);
    })();

    return () => {
      cancelled = true;
    };
  }, [company]);

  const syncPdfBrandingTemplates = async (
    companyId: string,
    primaryColor: string,
    footerNote: string | null,
  ) => {
    const { data: existingRows, error: existingError } = await supabase
      .from("pdf_templates" as never)
      .select("document_type")
      .eq("company_id", companyId)
      .in("document_type", [...PDF_BRANDING_DOCUMENT_TYPES]);

    if (existingError) return existingError;

    const existingTypes = new Set(
      ((existingRows ?? []) as Array<{ document_type?: string | null }>)
        .map((row) => row.document_type)
        .filter((value): value is string => Boolean(value)),
    );

    const missingRows = PDF_BRANDING_DOCUMENT_TYPES.filter(
      (documentType) => !existingTypes.has(documentType),
    ).map((document_type) => ({
      company_id: companyId,
      document_type,
      primary_color: primaryColor,
      footer_text: footerNote,
    }));

    if (missingRows.length > 0) {
      const { error: insertError } = await supabase
        .from("pdf_templates" as never)
        .insert(missingRows as never);
      if (insertError) return insertError;
    }

    const { error: updateError } = await supabase
      .from("pdf_templates" as never)
      .update({
        primary_color: primaryColor,
        footer_text: footerNote,
      } as never)
      .eq("company_id", companyId)
      .in("document_type", [...PDF_BRANDING_DOCUMENT_TYPES]);

    return updateError;
  };

  const savePdfBranding = async () => {
    if (!company) return;
    const hex = pdfPrimaryColor.trim();
    const footerNote = pdfFooterNote.trim() || null;

    if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
      toast.error(t("settings.pdfBranding.invalidColor", { defaultValue: "Cor inválida" }), {
        description: t("settings.pdfBranding.invalidColorDesc", {
          defaultValue: "Use o formato #RRGGBB (ex: #1e40af).",
        }),
      });
      return;
    }

    setSavingPdfBranding(true);

    const { error: companyError } = await supabase
      .from("companies")
      .update({
        pdf_primary_color: hex,
        pdf_footer_note: footerNote,
      } as never)
      .eq("id", company.id);

    if (companyError) {
      setSavingPdfBranding(false);
      toast.error(t("settings.saveFailed"), { description: companyError.message });
      return;
    }

    const templateError = await syncPdfBrandingTemplates(company.id, hex, footerNote);
    setSavingPdfBranding(false);

    if (templateError) {
      toast.error(t("settings.saveFailed"), { description: templateError.message });
      return;
    }

    toast.success(t("settings.saved"));
    await refresh();
  };

  const saveChartTheme = async (next: ChartThemeId): Promise<void> => {
    if (!company) return;
    setChartThemeState(next);
    setStoredChartTheme(next);
    setSavingChartTheme(true);
    const { error } = await supabase
      .from("companies")
      .update({ chart_theme: next } as never)
      .eq("id", company.id);
    setSavingChartTheme(false);
    if (error) {
      toast.error(t("settings.saveFailed"), { description: error.message });
      return;
    }
    toast.success(t("settings.saved"));
    await refresh();
  };

  const saveContactInfo = async () => {
    if (!company) return;

    // Validate address with Google Maps when address_line1 is filled
    let finalAddress = {
      address_line1: addressLine1.trim(),
      address_line2: addressLine2.trim(),
      city: city.trim(),
      state: state.trim(),
      postal_code: postalCode.trim(),
      country: country.trim(),
    };

    if (finalAddress.address_line1) {
      const { proceed, normalized } = await validateAddr({
        address_line1: finalAddress.address_line1,
        city: finalAddress.city,
        state: finalAddress.state,
        postal_code: finalAddress.postal_code,
        country: finalAddress.country,
      });
      if (!proceed) return;
      if (normalized) {
        finalAddress = {
          address_line1: normalized.address_line1,
          address_line2: finalAddress.address_line2,
          city: normalized.city,
          state: normalized.state,
          postal_code: normalized.postal_code,
          country: normalized.country,
        };
        // Reflect normalized values in form
        setAddressLine1(normalized.address_line1);
        setCity(normalized.city);
        setStateField(normalized.state);
        setPostalCode(normalized.postal_code);
        setCountry(normalized.country);
      }
    }

    setSavingContact(true);
    const { error } = await supabase
      .from("companies")
      .update({
        address_line1: finalAddress.address_line1 || null,
        address_line2: finalAddress.address_line2 || null,
        city: finalAddress.city || null,
        state: finalAddress.state || null,
        postal_code: finalAddress.postal_code || null,
        country: finalAddress.country || null,
        phone: phone.trim() || null,
        email: emailContact.trim() || null,
        website: website.trim() || null,
      } as never)
      .eq("id", company.id);
    setSavingContact(false);
    if (error) {
      toast.error(t("settings.saveFailed"), { description: error.message });
    } else {
      toast.success(t("settings.saved"));
      await refresh();
    }
  };

  const loadReports = async () => {
    if (!company || !isAdmin) return;
    setLoadingReports(true);
    const { data, error } = await supabase
      .from("gps_reports")
      .select(
        "id, period_start, period_end, storage_path, total_attempts, out_of_radius_count, generated_at",
      )
      .eq("company_id", company.id)
      .order("generated_at", { ascending: false })
      .limit(20);
    setLoadingReports(false);
    if (error) {
      toast.error(t("settings.loadReportsFailed"), { description: error.message });
      return;
    }
    setReports(data ?? []);
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, isAdmin]);

  const downloadReport = async (report: GpsReport) => {
    setDownloadingId(report.id);
    try {
      const { data, error } = await supabase.storage
        .from("gps-reports")
        .createSignedUrl(report.storage_path, 60);
      if (error) throw error;
      window.open(data.signedUrl, "_blank", "noopener");
    } catch (err) {
      toast.error(t("settings.downloadReportFailed"), {
        description: err instanceof Error ? err.message : t("settings.unknownError"),
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const generateNow = async () => {
    setGeneratingReport(true);
    try {
      await triggerGpsReport();
      toast.success(t("settings.reportGenerated"));
      await loadReports();
    } catch (err) {
      toast.error(t("settings.reportGenerateFailed"), {
        description: err instanceof Error ? err.message : t("settings.unknownError"),
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  const onLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !company) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("settings.logoTooBig"));
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${company.id}/logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("company-logos")
        .upload(path, file, { upsert: true, cacheControl: "3600" });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("company-logos").getPublicUrl(path);
      const { error: dbErr } = await supabase
        .from("companies")
        .update({ logo_url: pub.publicUrl })
        .eq("id", company.id);
      if (dbErr) throw dbErr;
      setLogoUrl(pub.publicUrl);
      await refresh();
      toast.success(t("settings.logoUpdated"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("settings.uploadFailed");
      toast.error(t("settings.logoUploadFailed"), { description: msg });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onSave = async () => {
    if (!company) return;
    if (!name.trim()) return toast.error(t("settings.companyNameRequired"));
    setSaving(true);
    const { error } = await supabase
      .from("companies")
      .update({
        name: name.trim(),
        owner_name: ownerName.trim() || null,
        timezone,
        currency,
        tax_percent: taxPercent,
        // TODO(types): remover cast após gen types (coluna ein nova em companies)
        ein: ein.trim() || null,
      } as never)
      .eq("id", company.id);
    setSaving(false);
    if (error) {
      toast.error(t("settings.saveFailed"), { description: error.message });
    } else {
      toast.success(t("settings.saved"));
      await refresh();
    }
  };

  const handleGenerateBackup = async () => {
    if (!company) return;
    setGeneratingBackup(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token)
        throw new Error(t("settings.backup.notAuthed", { defaultValue: "Sessão expirada" }));

      const res = await fetch(`/api/company-backup?companyId=${encodeURIComponent(company.id)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(await res.text());
      }

      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match?.[1] ?? `backup-${company.id}.zip`;

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(t("settings.backup.success", { defaultValue: "Backup gerado com sucesso" }));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(t("settings.backup.failed", { defaultValue: "Falha ao gerar backup" }), {
        description: msg,
      });
    } finally {
      setGeneratingBackup(false);
    }
  };

  // ---- Import wizard ----
  // Load destination (company) members lazily when the wizard opens.
  useEffect(() => {
    if (!importOpen || !company || !isAdmin || importMembers.length > 0) return;
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email")
        .eq("company_id", company.id)
        .eq("is_active", true)
        .order("full_name");
      if (cancelled || error) return;
      setImportMembers((data ?? []) as CompanyMember[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [importOpen, company, isAdmin, importMembers.length]);

  const resetImportWizard = () => {
    setImportStep(1);
    setImportFile(null);
    setImportSourceUsers([]);
    setImportChoices({});
    setImportClassification([]);
    setCreatedLogins([]);
    setImportDryRun(null);
    setImportDupConfirmed(false);
    setImportLoading(false);
  };

  const callImport = async (
    mode: "dry-run" | "commit",
    choices: Record<string, ImportChoice>,
  ): Promise<unknown> => {
    if (!company)
      throw new Error(t("settings.import.notAuthed", { defaultValue: "Sessão expirada" }));
    if (!importFile)
      throw new Error(t("settings.import.noFile", { defaultValue: "Selecione um arquivo .zip" }));
    const { data: sess } = await supabase.auth.getSession();
    const token = sess.session?.access_token;
    if (!token)
      throw new Error(t("settings.import.notAuthed", { defaultValue: "Sessão expirada" }));

    const form = new FormData();
    form.append("file", importFile);
    form.append("choices", JSON.stringify(choices));

    const res = await fetch(
      `/api/company-import?companyId=${encodeURIComponent(company.id)}&mode=${mode}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      },
    );
    if (!res.ok) {
      throw new Error(await res.text());
    }
    return res.json();
  };

  // Step 1 → 2: fetch the source users list via an empty dry-run.
  const handleImportContinueFromFile = async () => {
    setImportLoading(true);
    try {
      const result = (await callImport("dry-run", {})) as ImportDryRun;
      setImportSourceUsers(result.sourceUsers ?? []);
      const classification = result.classification ?? [];
      setImportClassification(classification);
      // Seed default choices from the backend classification.
      const seed: Record<string, ImportChoice> = {};
      for (const c of classification) {
        if (c.status === "mappable") {
          seed[c.sourceId] = { mode: "map", targetId: c.suggestedTargetId };
        } else if (c.status === "creatable") {
          seed[c.sourceId] = { mode: "create" };
        } else if (c.status === "elsewhere") {
          seed[c.sourceId] = { mode: "move" };
        } else {
          seed[c.sourceId] = { mode: "none" };
        }
      }
      setImportChoices(seed);
      setImportDryRun(result);
      setImportStep(2);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(t("settings.import.dryRunFailed", { defaultValue: "Falha ao ler o backup" }), {
        description: msg,
      });
    } finally {
      setImportLoading(false);
    }
  };

  const setImportChoice = (sourceId: string, choice: ImportChoice) => {
    setImportChoices((prev) => ({ ...prev, [sourceId]: choice }));
  };

  // Step 2 → 3: re-run dry-run with the chosen user choices to get the review.
  const handleImportReview = async () => {
    setImportLoading(true);
    try {
      const result = (await callImport("dry-run", importChoices)) as ImportDryRun;
      setImportDryRun(result);
      setImportDupConfirmed(false);
      setImportStep(3);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(t("settings.import.dryRunFailed", { defaultValue: "Falha ao ler o backup" }), {
        description: msg,
      });
    } finally {
      setImportLoading(false);
    }
  };

  const formatBytes = (n: number) =>
    n >= 1024 * 1024
      ? `${(n / 1024 / 1024).toFixed(1)} MB`
      : `${Math.max(1, Math.round(n / 1024))} KB`;

  // Step 3 → commit.
  const handleImportCommit = async () => {
    setImportLoading(true);
    try {
      const result = (await callImport("commit", importChoices)) as {
        inserted: Record<string, number>;
        filesUploaded?: number;
        filesMissing?: number;
        createdLogins?: CreatedLogin[];
      };
      const total = Object.values(result.inserted ?? {}).reduce(
        (acc, n) => acc + Number(n || 0),
        0,
      );
      let description = t("settings.import.commitSuccessDesc", {
        defaultValue: "{{count}} registros inseridos.",
        count: total,
      });
      if (typeof result.filesUploaded === "number") {
        description +=
          " " +
          t("settings.import.commitFilesDesc", {
            defaultValue: "{{count}} arquivo(s) enviado(s).",
            count: result.filesUploaded,
          });
      }
      toast.success(t("settings.import.commitSuccess", { defaultValue: "Importação concluída" }), {
        description,
      });
      const logins = result.createdLogins ?? [];
      if (logins.length > 0) {
        // Keep the dialog open so the admin can copy the one-time passwords.
        setCreatedLogins(logins);
        setImportStep(5);
      } else {
        setImportOpen(false);
        resetImportWizard();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(t("settings.import.commitFailed", { defaultValue: "Falha ao importar" }), {
        description: msg,
      });
    } finally {
      setImportLoading(false);
    }
  };

  const handleCopyLogins = async () => {
    const block = createdLogins.map((l) => `${l.email}\t${l.tempPassword}`).join("\n");
    try {
      await navigator.clipboard.writeText(block);
      toast.success(t("settings.import.copied", { defaultValue: "Senhas copiadas" }));
    } catch {
      toast.error(t("settings.import.commitFailed", { defaultValue: "Falha ao importar" }));
    }
  };

  const handleDeleteAccount = () => {
    if (!company) return;
    const confirmKeyword = t("settings.deleteAccount.confirmKeyword", { defaultValue: "EXCLUIR" });
    if (deleteConfirmText !== confirmKeyword) {
      toast.error(
        t("settings.deleteAccount.invalidConfirm", {
          defaultValue: `Digite "${confirmKeyword}" para confirmar`,
        }),
      );
      return;
    }
    setDeletingAccount(true);
    deleteCompanyAccount({
      data: { companyId: company.id, confirmation: deleteConfirmText },
    })
      .then(() => {
        toast.success(
          t("settings.deleteAccount.success", {
            defaultValue: "Conta excluída com sucesso",
          }),
        );
        return supabase.auth.signOut();
      })
      .then(() => {
        navigate({ to: "/" });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(
          t("settings.deleteAccount.failed", {
            defaultValue: "Falha ao excluir a conta",
          }),
          { description: msg },
        );
        setDeletingAccount(false);
      });
  };

  const saveGpsConfig = async () => {
    if (!company) return;
    if (!Number.isFinite(gpsRadius) || gpsRadius <= 0 || gpsRadius > 10000) {
      return toast.error(t("settings.gpsConfig.invalidRadius"));
    }
    setSavingGps(true);
    const { error } = await supabase
      .from("companies")
      .update({
        gps_default_radius_meters: Math.round(gpsRadius),
        require_job_site_gps: requireGps,
      })
      .eq("id", company.id);
    setSavingGps(false);
    if (error) {
      toast.error(t("settings.saveFailed"), { description: error.message });
    } else {
      toast.success(t("settings.saved"));
      await refresh();
    }
  };

  const saveLanguage = async () => {
    if (pendingLang === currentLang) {
      toast.info(t("settings.saved"));
      return;
    }
    setSavingLang(true);
    try {
      // 1) Persist on the user profile so it follows the user across devices.
      if (user?.id) {
        const { error: profileError } = await supabase
          .from("profiles")
          .update({ language: pendingLang })
          .eq("id", user.id);
        if (profileError) throw profileError;
      }
      // 2) Apply immediately to the active session.
      await loadLanguage(pendingLang);
      await i18n.changeLanguage(pendingLang);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem("app.lang", pendingLang);
        } catch {
          // ignore storage errors (private mode, etc.)
        }
      }
      if (typeof document !== "undefined") {
        document.documentElement.lang = pendingLang;
      }
      toast.success(t("settings.saved"));
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Could not save";
      toast.error(t("settings.saveFailed"), { description: msg });
    } finally {
      setSavingLang(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("settings.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("settings.subtitle")}</p>
      </div>

      {!isAdmin && (
        <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
          {t("settings.needAdmin")}
        </div>
      )}

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-5">
          <TabsTrigger value="general" className="gap-1.5">
            <Settings2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t("settings.tabs.general")}</span>
            <span className="sm:hidden">{t("settings.tabs.general")}</span>
          </TabsTrigger>
          <TabsTrigger value="company" className="gap-1.5">
            <Building2 className="h-3.5 w-3.5" />
            {t("settings.tabs.company")}
          </TabsTrigger>
          <TabsTrigger value="branding" className="gap-1.5">
            <Palette className="h-3.5 w-3.5" />
            {t("settings.tabs.branding")}
          </TabsTrigger>
          <TabsTrigger value="gps" className="gap-1.5">
            <Navigation className="h-3.5 w-3.5" />
            {t("settings.tabs.gps")}
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-1.5">
            <Bell className="h-3.5 w-3.5" />
            {t("settings.tabs.alerts")}
          </TabsTrigger>
        </TabsList>

        {/* ================ GENERAL TAB ================ */}
        <TabsContent value="general" className="space-y-6">
          {/* Language */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <Globe className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold">{t("settings.language")}</p>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">{t("settings.languageDesc")}</p>
            <div className="flex max-w-xs flex-col gap-3">
              <Select
                value={pendingLang}
                onValueChange={(v) => setPendingLang(v as SupportedLanguage)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lng) => (
                    <SelectItem key={lng} value={lng}>
                      {LANGUAGE_LABELS[lng]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={saveLanguage}
                disabled={savingLang || pendingLang === currentLang}
                size="sm"
                className="self-start"
              >
                {savingLang && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                {t("settings.save")}
              </Button>
            </div>
          </div>

          {/* Company defaults: name, currency, timezone, tax, plan */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <Settings2 className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-semibold">{t("settings.companyName")}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t("settings.companyName")}</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} disabled={!isAdmin} />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t("settings.ownerName", { defaultValue: "Nome do proprietário" })}</Label>
                <Input
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  disabled={!isAdmin}
                  placeholder={t("settings.ownerNamePlaceholder", {
                    defaultValue: "Nome completo do proprietário",
                  })}
                />
              </div>

              <div className="space-y-1.5">
                <Label>{t("settings.currency")}</Label>
                <Select value={currency} onValueChange={setCurrency} disabled={!isAdmin}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t("settings.timezone")}</Label>
                <Select value={timezone} onValueChange={setTimezone} disabled={!isAdmin}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t("settings.defaultTax")}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={taxPercent}
                  onChange={(e) => setTaxPercent(Number(e.target.value) || 0)}
                  disabled={!isAdmin}
                />
              </div>

              <div className="space-y-1.5">
                <Label>{t("settings.plan")}</Label>
                <Input value={company?.plan ?? "starter"} disabled className="capitalize" />
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label>
                  {t("pages.settings.company.ein", {
                    defaultValue: "EIN (Employer Identification Number)",
                  })}
                </Label>
                <Input
                  value={ein}
                  onChange={(e) => setEin(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="12-3456789"
                />
                <p className="text-xs text-muted-foreground">
                  {t("pages.settings.company.einHint", {
                    defaultValue: "Necessário para emitir o 1099 dos autônomos.",
                  })}
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={onSave} disabled={!isAdmin || saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {saving ? t("common.saving") : t("common.saveChanges")}
              </Button>
            </div>
          </div>

          {/* Backup card */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="mb-3 flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-base font-semibold">
                {t("settings.backup.title", { defaultValue: "Backup de dados" })}
              </h2>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
              {t("settings.backup.description", {
                defaultValue:
                  "Baixe um pacote de backup (.zip) com todas as informações da sua empresa (tabelas + arquivos) para arquivamento ou restauração.",
              })}
            </p>
            <div className="flex justify-end">
              <Button
                onClick={handleGenerateBackup}
                disabled={!isAdmin || generatingBackup}
                variant="outline"
              >
                {generatingBackup ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="mr-2 h-4 w-4" />
                )}
                {generatingBackup
                  ? t("settings.backup.generating", { defaultValue: "Gerando backup..." })
                  : t("settings.backup.download", { defaultValue: "Baixar backup (.zip)" })}
              </Button>
            </div>
          </div>

          {/* Import backup card */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-violet-500/15 text-violet-600 dark:text-violet-400">
                <Upload className="h-4 w-4" />
              </span>
              <h2 className="text-base font-semibold">
                {t("settings.import.title", { defaultValue: "Importar backup" })}
              </h2>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
              {t("settings.import.description", {
                defaultValue:
                  "Importe um pacote de backup (.zip) para dentro desta empresa. Você mapeia os usuários e revisa o que será inserido antes de confirmar.",
              })}
            </p>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  resetImportWizard();
                  setImportOpen(true);
                }}
                disabled={!isAdmin}
                variant="outline"
                className="border-violet-500/40 text-violet-700 hover:bg-violet-500/10 dark:text-violet-300"
              >
                <Upload className="mr-2 h-4 w-4" />
                {t("settings.import.open", { defaultValue: "Importar backup (.zip)" })}
              </Button>
            </div>
          </div>

          {/* Danger zone — exclusão de conta */}
          <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-5">
            <div className="mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <h2 className="text-base font-semibold text-destructive">
                {t("settings.deleteAccount.title", {
                  defaultValue: "Zona de perigo — Excluir conta",
                })}
              </h2>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
              {t("settings.deleteAccount.description", {
                defaultValue:
                  "Esta ação apaga PERMANENTEMENTE todos os dados da empresa: clientes, projetos, faturas, contratos, horas, despesas, equipe, assinaturas e o login de todos os usuários. Não há como recuperar. Recomendamos baixar um backup antes.",
              })}
            </p>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setDeleteConfirmText("");
                  setDeleteDialogOpen(true);
                }}
                disabled={!isAdmin}
                variant="destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("settings.deleteAccount.button", {
                  defaultValue: "Excluir conta permanentemente",
                })}
              </Button>
            </div>
          </div>
        </TabsContent>
        <TabsContent value="company" className="space-y-6">
          {/* Logo */}
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="mb-4 text-sm font-semibold">{t("settings.companyLogo")}</p>
            <div className="flex items-center gap-4">
              <div className="flex h-20 w-20 items-center justify-center rounded-lg border border-border bg-muted/30">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                ) : (
                  <Building2 className="h-8 w-8 text-muted-foreground" />
                )}
              </div>
              <div>
                <Label
                  htmlFor="logo-upload"
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent ${
                    !isAdmin || uploading ? "pointer-events-none opacity-50" : ""
                  }`}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  {uploading ? t("common.uploading") : t("settings.uploadNewLogo")}
                </Label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  className="sr-only"
                  onChange={onLogoChange}
                  disabled={!isAdmin || uploading}
                />
                <p className="mt-1.5 text-xs text-muted-foreground">{t("settings.logoHint")}</p>
              </div>
            </div>
          </div>

          {/* Contact info */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="mb-4 flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-semibold">{t("settings.contactInfo")}</p>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">{t("settings.contactInfoHint")}</p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t("settings.addressLine1")}</Label>
                <Input
                  value={addressLine1}
                  onChange={(e) => setAddressLine1(e.target.value)}
                  disabled={!isAdmin}
                  placeholder={t("settings.addressPlaceholders.line1")}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t("settings.addressLine2")}</Label>
                <Input
                  value={addressLine2}
                  onChange={(e) => setAddressLine2(e.target.value)}
                  disabled={!isAdmin}
                  placeholder={t("settings.addressPlaceholders.line2")}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("settings.city")}</Label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} disabled={!isAdmin} />
              </div>
              <div className="space-y-1.5">
                <Label>{t("settings.state")}</Label>
                <Input
                  value={state}
                  onChange={(e) => setStateField(e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("settings.postalCode")}</Label>
                <Input
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("settings.country")}</Label>
                <Input
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("settings.phone")}</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t("settings.contactEmail")}</Label>
                <Input
                  type="email"
                  value={emailContact}
                  onChange={(e) => setEmailContact(e.target.value)}
                  disabled={!isAdmin}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  {t("settings.website")}
                </Label>
                <Input
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  disabled={!isAdmin}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                onClick={saveContactInfo}
                disabled={!isAdmin || savingContact || validatingAddr}
              >
                {(savingContact || validatingAddr) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {validatingAddr
                  ? t("address.validating", { defaultValue: "Validando endereço..." })
                  : t("settings.saveContactInfo")}
              </Button>
            </div>
          </div>
          <AddressValidationDialog {...addrDialogProps} />
        </TabsContent>

        {/* ================ BRANDING / PDF TAB ================ */}
        <TabsContent value="branding" className="space-y-6">
          <CollapsibleCard
            title={t("settings.pdfBranding.title", { defaultValue: "Personalização do PDF" })}
            icon={<FileDown className="h-4 w-4 text-muted-foreground" />}
            description={t("settings.pdfBranding.description", {
              defaultValue:
                "Estas configurações aparecem em todos os PDFs gerados (orçamentos, faturas, relatórios).",
            })}
            defaultOpen={false}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>
                  {t("settings.pdfBranding.primaryColor", { defaultValue: "Cor de destaque" })}
                </Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(pdfPrimaryColor) ? pdfPrimaryColor : "#222933"}
                    onChange={(e) => setPdfPrimaryColor(e.target.value)}
                    disabled={!isAdmin}
                    className="h-10 w-14 cursor-pointer rounded-md border border-input bg-background p-1 disabled:cursor-not-allowed disabled:opacity-50"
                    aria-label={t("settings.pdfBranding.primaryColor", {
                      defaultValue: "Cor de destaque",
                    })}
                  />
                  <Input
                    value={pdfPrimaryColor}
                    onChange={(e) => setPdfPrimaryColor(e.target.value)}
                    disabled={!isAdmin}
                    placeholder="#222933"
                    className="font-mono"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("settings.pdfBranding.primaryColorHelp", {
                    defaultValue: "Usada no nome da empresa e no título do relatório.",
                  })}
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>
                  {t("settings.pdfBranding.footerNote", { defaultValue: "Nota do rodapé" })}
                </Label>
                <Textarea
                  value={pdfFooterNote}
                  onChange={(e) => setPdfFooterNote(e.target.value.slice(0, 140))}
                  disabled={!isAdmin}
                  placeholder={t("settings.pdfBranding.footerNotePlaceholder", {
                    defaultValue: "Ex: CNPJ 00.000.000/0001-00 — Documento sem valor fiscal",
                  })}
                  rows={3}
                  maxLength={140}
                />
                <p className="text-xs text-muted-foreground">
                  {pdfFooterNote.length}/140 ·{" "}
                  {t("settings.pdfBranding.footerNoteHelp", {
                    defaultValue: "Aparece centralizado na base de cada página.",
                  })}
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={savePdfBranding} disabled={!isAdmin || savingPdfBranding}>
                {savingPdfBranding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("settings.pdfBranding.save", { defaultValue: "Salvar personalização" })}
              </Button>
            </div>
          </CollapsibleCard>

          {/* Tooltip legend / explanation */}
          <CollapsibleCard
            title={t("settings.cashFlowLegend.title")}
            icon={<Info className="h-4 w-4 text-muted-foreground" />}
            description={t("settings.cashFlowLegend.description")}
            defaultOpen={false}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500" />
                  <span className="text-sm font-semibold">
                    {t("settings.cashFlowLegend.income.label")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("settings.cashFlowLegend.income.desc")}
                </p>
              </div>

              <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm bg-rose-500" />
                  <span className="text-sm font-semibold">
                    {t("settings.cashFlowLegend.outflow.label")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("settings.cashFlowLegend.outflow.desc")}
                </p>
              </div>

              <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-xs font-bold tabular-nums text-muted-foreground">%</span>
                  <span className="text-sm font-semibold">
                    {t("settings.cashFlowLegend.share.label")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("settings.cashFlowLegend.share.desc")}
                </p>
              </div>

              <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-sm font-bold text-emerald-500">+</span>
                  <span className="text-sm font-semibold">
                    {t("settings.cashFlowLegend.netResult.label")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("settings.cashFlowLegend.netResult.desc")}
                </p>
              </div>

              <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-xs font-bold tabular-nums text-muted-foreground">%</span>
                  <span className="text-sm font-semibold">
                    {t("settings.cashFlowLegend.margin.label")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("settings.cashFlowLegend.margin.desc")}
                </p>
              </div>

              <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                <div className="mb-1 flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                  <span className="text-sm font-semibold">
                    {t("settings.cashFlowLegend.accumulated.label")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("settings.cashFlowLegend.accumulated.desc")}
                </p>
              </div>

              <div className="rounded-md border border-border/60 bg-muted/30 p-3 sm:col-span-2">
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-sm font-semibold">
                    {t("settings.cashFlowLegend.extremes.label")}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("settings.cashFlowLegend.extremes.desc")}
                </p>
              </div>
            </div>
          </CollapsibleCard>

          {/* Chart theme picker */}
          <CollapsibleCard
            title={t("settings.chartTheme.title", { defaultValue: "Estilo dos gráficos" })}
            icon={<Palette className="h-4 w-4 text-muted-foreground" />}
            description={t("settings.chartTheme.description", {
              defaultValue:
                "Escolha a aparência aplicada a todos os gráficos do sistema. A mudança é imediata.",
            })}
            rightSlot={
              savingChartTheme ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : null
            }
            defaultOpen
          >
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("settings.chartTheme.quickSelect")}
              </p>
              <ChartThemePickerCompact
                value={chartTheme}
                onChange={saveChartTheme}
                disabled={!isAdmin || savingChartTheme}
              />
            </div>
          </CollapsibleCard>

          {isAdmin && <EstimatePdfTemplateEditor />}
        </TabsContent>

        {/* ================ GPS TAB ================ */}
        <TabsContent value="gps" className="space-y-6">
          {isAdmin && (
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="mb-4 flex items-center gap-2">
                <Navigation className="h-4 w-4 text-primary" />
                <p className="text-sm font-semibold">{t("settings.gpsConfig.title")}</p>
              </div>
              <p className="mb-4 text-xs text-muted-foreground">
                {t("settings.gpsConfig.description")}
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t("settings.gpsConfig.radius")}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={10000}
                    step={10}
                    value={gpsRadius}
                    onChange={(e) => setGpsRadius(Number(e.target.value) || 0)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("settings.gpsConfig.radiusHelp")}
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("settings.gpsConfig.require")}</Label>
                  <div className="flex h-9 items-center gap-3 rounded-md border border-input bg-background px-3">
                    <Switch checked={requireGps} onCheckedChange={setRequireGps} />
                    <span className="text-sm text-muted-foreground">
                      {requireGps
                        ? t("settings.gpsConfig.required")
                        : t("settings.gpsConfig.optional")}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t("settings.gpsConfig.requireHelp")}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button onClick={saveGpsConfig} disabled={savingGps} size="sm">
                  {savingGps && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                  {t("common.saveChanges")}
                </Button>
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <p className="flex items-center gap-2 text-sm font-semibold">
                    <MapPin className="h-4 w-4" />
                    {t("settings.gpsReports")}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("settings.gpsReportsDesc")}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generateNow}
                  disabled={generatingReport}
                >
                  {generatingReport ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {t("settings.generateNow")}
                </Button>
              </div>

              {loadingReports ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
                </div>
              ) : reports.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("settings.noReports")}</p>
              ) : (
                <ul className="divide-y divide-border">
                  {reports.map((r) => {
                    const pct =
                      r.total_attempts > 0
                        ? ((r.out_of_radius_count / r.total_attempts) * 100).toFixed(1)
                        : "0.0";
                    return (
                      <li key={r.id} className="flex items-center justify-between gap-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">
                            {r.period_start} → {r.period_end}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("settings.attempts", { count: r.total_attempts })} ·{" "}
                            {t("settings.outOfRadius", { count: r.out_of_radius_count })} ({pct}%) ·{" "}
                            {t("settings.generated")} {new Date(r.generated_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadReport(r)}
                          disabled={downloadingId === r.id}
                        >
                          {downloadingId === r.id ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <FileDown className="mr-2 h-4 w-4" />
                          )}
                          {t("common.download")}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {!isAdmin && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              {t("settings.needAdmin")}
            </div>
          )}
        </TabsContent>

        {/* ================ ALERTS TAB ================ */}
        <TabsContent value="alerts" className="space-y-6">
          <AlertSettingsEditor />
          <WhatsAppSettingsEditor />
          {!isAdmin && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
              {t("settings.needAdmin")}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ================ IMPORT WIZARD DIALOG ================ */}
      <Dialog
        open={importOpen}
        onOpenChange={(open) => {
          if (importLoading) return;
          setImportOpen(open);
          if (!open) resetImportWizard();
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-4 w-4 text-violet-500" />
              {t("settings.import.title", { defaultValue: "Importar backup" })}
            </DialogTitle>
            <DialogDescription>
              {importStep === 5 ? (
                t("settings.import.createdLoginsTitle", { defaultValue: "Logins criados" })
              ) : (
                <>
                  {t("settings.import.stepIndicator", {
                    defaultValue: "Passo {{current}} de 4",
                    current: importStep,
                  })}
                  {" · "}
                  {importStep === 1 && t("settings.import.step1Label", { defaultValue: "Arquivo" })}
                  {importStep === 2 &&
                    t("settings.import.step2Label", { defaultValue: "De-para de usuários" })}
                  {importStep === 3 && t("settings.import.step3Label", { defaultValue: "Revisão" })}
                  {importStep === 4 &&
                    t("settings.import.step4Label", { defaultValue: "Confirmar" })}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* STEP 1 — file */}
          {importStep === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("settings.import.step1Help", {
                  defaultValue:
                    "Selecione o arquivo .zip gerado pelo backup. Ele será lido no servidor para listar os usuários de origem.",
                })}
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="import-file">
                  {t("settings.import.fileLabel", { defaultValue: "Arquivo de backup (.zip)" })}
                </Label>
                <input
                  id="import-file"
                  type="file"
                  accept=".zip,application/zip"
                  onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-muted-foreground file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-violet-500/15 file:px-3 file:py-2 file:text-sm file:font-medium file:text-violet-700 hover:file:bg-violet-500/25 dark:file:text-violet-300"
                />
                {importFile && (
                  <p className="text-xs text-muted-foreground">
                    {importFile.name} · {(importFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
              </div>
            </div>
          )}

          {/* STEP 2 — user mapping / login choices */}
          {importStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("settings.import.step2Help", {
                  defaultValue:
                    "Para cada usuário do backup, escolha mapear para um membro desta empresa ou criar um login novo. Usuários sem correspondência possível são ignorados.",
                })}
              </p>
              {importClassification.length === 0 ? (
                <p className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                  {t("settings.import.noSourceUsers", {
                    defaultValue: "Nenhum usuário encontrado no backup.",
                  })}
                </p>
              ) : (
                <div className="space-y-3">
                  {importClassification.map((c) => {
                    const su = importSourceUsers.find((u) => u.id === c.sourceId);
                    const displayName = su?.full_name || su?.email || c.email || c.sourceId;
                    const displayEmail = su?.email || c.email;
                    const choice = importChoices[c.sourceId] ?? { mode: "none" };
                    const mappableOrCreatable = c.status === "mappable" || c.status === "creatable";
                    return (
                      <div
                        key={c.sourceId}
                        className="flex flex-col gap-2 rounded-md border border-border bg-card p-3 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{displayName}</p>
                          {displayEmail && (
                            <p className="truncate text-xs text-muted-foreground">{displayEmail}</p>
                          )}
                          {c.status === "mappable" && (
                            <p className="mt-0.5 text-xs text-emerald-600 dark:text-emerald-400">
                              {t("settings.import.emailExists", {
                                defaultValue: "e-mail já existe no destino",
                              })}
                            </p>
                          )}
                        </div>
                        <div className="sm:w-64">
                          {mappableOrCreatable ? (
                            <div className="flex flex-col gap-2">
                              <label className="flex items-center gap-2 text-xs">
                                <Switch
                                  checked={choice.mode === "create"}
                                  onCheckedChange={(on) => {
                                    if (on) {
                                      setImportChoice(c.sourceId, { mode: "create" });
                                    } else {
                                      setImportChoice(c.sourceId, {
                                        mode: "map",
                                        targetId: c.suggestedTargetId ?? importMembers[0]?.id,
                                      });
                                    }
                                  }}
                                />
                                <span>
                                  {t("settings.import.createNewOption", {
                                    defaultValue: "Criar login novo",
                                  })}
                                </span>
                              </label>
                              {choice.mode === "map" && (
                                <Select
                                  value={choice.targetId ?? ""}
                                  onValueChange={(v) =>
                                    setImportChoice(c.sourceId, { mode: "map", targetId: v })
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue
                                      placeholder={t("settings.import.mapExistingOption", {
                                        defaultValue: "Mapear para membro existente",
                                      })}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {importMembers.map((m) => (
                                      <SelectItem key={m.id} value={m.id}>
                                        {m.full_name || m.email || m.id}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          ) : c.status === "elsewhere" ? (
                            <div className="flex flex-col gap-1">
                              <label className="flex items-center gap-2 text-xs">
                                <Switch
                                  checked={choice.mode === "move"}
                                  onCheckedChange={(on) => {
                                    setImportChoice(
                                      c.sourceId,
                                      on ? { mode: "move" } : { mode: "none" },
                                    );
                                  }}
                                />
                                <span>
                                  {t("settings.import.moveToCompany", {
                                    defaultValue: "Mover para esta empresa",
                                  })}
                                </span>
                              </label>
                              {choice.mode === "move" ? (
                                <p className="text-xs text-muted-foreground">
                                  {t("settings.import.moveHint", {
                                    defaultValue:
                                      "A conta existe em outra empresa; será movida para cá (sai da origem). Entra como employee.",
                                  })}
                                </p>
                              ) : (
                                <span className="inline-flex w-fit items-center rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground">
                                  {t("settings.import.userNotMappable", {
                                    defaultValue: "não mapear — dados ignorados",
                                  })}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground">
                              {t("settings.import.userNotMappable", {
                                defaultValue: "não mapear — dados ignorados",
                              })}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 3 — review (dry-run) */}
          {importStep === 3 && importDryRun && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t("settings.import.step3Help", {
                  defaultValue:
                    "Confira quantas linhas serão inseridas em cada tabela e o que já existe no destino. Nada é gravado ainda.",
                })}
              </p>

              {importDryRun.mode && (
                <p className="flex items-center gap-2 text-sm font-medium">
                  {importDryRun.mode === "restore"
                    ? t("settings.import.modeRestore", {
                        defaultValue: "Restaurando esta empresa",
                      })
                    : t("settings.import.modeTransfer", {
                        defaultValue: "Transferindo de outra empresa",
                      })}
                </p>
              )}

              {(() => {
                const alreadyExistedCount = importDryRun.report.skipped.filter(
                  (s) => s.reason === "already exists",
                ).length;
                return (
                  alreadyExistedCount > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {t("settings.import.alreadyExisted", { count: alreadyExistedCount })}
                    </p>
                  )
                );
              })()}

              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left">
                      <th className="px-3 py-2 font-medium">
                        {t("settings.import.colTable", { defaultValue: "Tabela" })}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t("settings.import.colToInsert", { defaultValue: "A inserir" })}
                      </th>
                      <th className="px-3 py-2 text-right font-medium">
                        {t("settings.import.colCurrent", { defaultValue: "Atual no destino" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(importDryRun.report.counts)
                      .filter(([, n]) => Number(n) > 0)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([table, n]) => (
                        <tr key={table} className="border-b border-border/60 last:border-0">
                          <td className="px-3 py-2 font-mono text-xs">{table}</td>
                          <td className="px-3 py-2 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                            +{n}
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground">
                            {importDryRun.targetCounts[table] ?? 0}
                          </td>
                        </tr>
                      ))}
                    {Object.values(importDryRun.report.counts).every((n) => Number(n) === 0) && (
                      <tr>
                        <td
                          colSpan={3}
                          className="px-3 py-3 text-center text-sm text-muted-foreground"
                        >
                          {t("settings.import.noRows", {
                            defaultValue: "Nenhuma linha nova para inserir.",
                          })}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-muted-foreground">
                {t("settings.import.usersSummary", {
                  defaultValue:
                    "{{create}} a criar · {{move}} a mover · {{map}} mapeado(s) · {{skip}} não mapeável(is)",
                  create: importDryRun.report.usersToCreate ?? 0,
                  move: importDryRun.report.usersToMove ?? 0,
                  map: importDryRun.report.usersToMap ?? 0,
                  skip: importDryRun.report.usersNotMappable ?? 0,
                })}
              </p>

              {importDryRun.report.warnings.length > 0 && (
                <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4" />
                    {t("settings.import.warningsTitle", { defaultValue: "Avisos" })}
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-xs text-amber-800 dark:text-amber-300">
                    {importDryRun.report.warnings.map((w, i) => (
                      <li key={i}>{describeUnknown(w)}</li>
                    ))}
                  </ul>
                  <label className="mt-1 flex items-start gap-2 text-xs text-amber-900 dark:text-amber-200">
                    <Checkbox
                      checked={importDupConfirmed}
                      onCheckedChange={(c) => setImportDupConfirmed(c === true)}
                      className="mt-0.5"
                    />
                    <span>
                      {t("settings.import.dupConfirm", {
                        defaultValue: "Entendo que isto pode duplicar os dados e desejo continuar.",
                      })}
                    </span>
                  </label>
                </div>
              )}

              {importDryRun.report.skipped.length > 0 && (
                <div className="rounded-md border border-border bg-muted/30 p-3">
                  <p className="mb-1 text-sm font-medium">
                    {t("settings.import.skippedTitle", { defaultValue: "Itens ignorados" })}
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                    {importDryRun.report.skipped.map((s, i) => (
                      <li key={i}>{describeUnknown(s)}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(importDryRun.report.renumbered?.length ?? 0) > 0 && (
                <div className="space-y-2 rounded-md border border-blue-500/40 bg-blue-500/10 p-3">
                  <p className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-400">
                    <RefreshCw className="h-4 w-4" />
                    {t("settings.import.renumberedTitle", {
                      defaultValue: "Documentos renumerados",
                    })}
                  </p>
                  <p className="text-xs text-blue-800 dark:text-blue-300">
                    {t("settings.import.renumberedDesc", {
                      defaultValue:
                        "Estes números já existiam no destino e receberão uma numeração nova:",
                    })}
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-xs text-blue-800 dark:text-blue-300">
                    {importDryRun.report.renumbered!.slice(0, 20).map((r, i) => (
                      <li key={i}>
                        <span className="font-mono">{r.table}</span>: {r.from} → {r.to}
                      </li>
                    ))}
                  </ul>
                  {importDryRun.report.renumbered!.length > 20 && (
                    <p className="pl-5 text-xs text-blue-700 dark:text-blue-400">
                      {t("settings.import.renumberedMore", {
                        defaultValue: "+{{count}} mais",
                        count: importDryRun.report.renumbered!.length - 20,
                      })}
                    </p>
                  )}
                </div>
              )}

              {(importDryRun.report.configSkipped?.length ?? 0) > 0 && (
                <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <Info className="h-4 w-4" />
                    {t("settings.import.configSkippedTitle", {
                      defaultValue: "Configurações puladas (já existem)",
                    })}
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-xs text-amber-800 dark:text-amber-300">
                    {importDryRun.report.configSkipped!.map((c, i) => (
                      <li key={i}>
                        <span className="font-mono">{c.table}</span>: {c.value}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {typeof importDryRun.report.dedupedCount === "number" &&
                importDryRun.report.dedupedCount > 0 && (
                  <div className="space-y-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3">
                    <p className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="h-4 w-4" />
                      {t("settings.import.dedupedCount", {
                        defaultValue:
                          "{{count}} registro(s) já existiam no destino e serão reaproveitados (não duplicados)",
                        count: importDryRun.report.dedupedCount,
                      })}
                    </p>
                  </div>
                )}

              {typeof importDryRun.report.filesToUpload === "number" &&
                importDryRun.report.filesToUpload >= 0 && (
                  <div className="space-y-2 rounded-md border border-violet-500/40 bg-violet-500/10 p-3">
                    <p className="flex items-center gap-2 text-sm font-medium text-violet-700 dark:text-violet-400">
                      <Upload className="h-4 w-4" />
                      {t("settings.import.filesToUpload", {
                        defaultValue: "{{count}} arquivo(s) serão enviados ({{size}})",
                        count: importDryRun.report.filesToUpload ?? 0,
                        size: formatBytes(importDryRun.report.bytesToUpload ?? 0),
                      })}
                    </p>
                  </div>
                )}

              {(importDryRun.report.filesMissing ?? 0) > 0 && (
                <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
                  <p className="flex items-center gap-2 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <Info className="h-4 w-4" />
                    {t("settings.import.filesMissingWarn", {
                      defaultValue:
                        "{{count}} arquivo(s) do backup não foram encontrados e serão ignorados",
                      count: importDryRun.report.filesMissing,
                    })}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 4 — confirm */}
          {importStep === 4 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-md border border-violet-500/40 bg-violet-500/10 p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-violet-600 dark:text-violet-400" />
                <div>
                  <p className="text-sm font-medium">
                    {t("settings.import.confirmTitle", {
                      defaultValue: "Pronto para importar",
                    })}
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t("settings.import.confirmBody", {
                      defaultValue:
                        "Ao confirmar, os dados serão inseridos nesta empresa. A operação é transacional: se algo falhar, nada é gravado.",
                    })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5 — created logins (only when the commit created new logins) */}
          {importStep === 5 && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 rounded-md border border-amber-500/50 bg-amber-500/10 p-4">
                <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                  {t("settings.import.passwordsWarn", {
                    defaultValue: "Copie as senhas agora — elas não reaparecem.",
                  })}
                </p>
              </div>

              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-left">
                      <th className="px-3 py-2 font-medium">
                        {t("settings.import.colEmail", { defaultValue: "E-mail" })}
                      </th>
                      <th className="px-3 py-2 font-medium">
                        {t("settings.import.colPassword", { defaultValue: "Senha temporária" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {createdLogins.map((l, i) => (
                      <tr key={i} className="border-b border-border/60 last:border-0">
                        <td className="px-3 py-2">{l.email}</td>
                        <td className="px-3 py-2 font-mono text-xs">{l.tempPassword}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Button type="button" variant="outline" onClick={handleCopyLogins}>
                <Copy className="mr-2 h-4 w-4" />
                {t("settings.import.copyAll", { defaultValue: "Copiar tudo" })}
              </Button>
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between">
            <div>
              {importStep > 1 && importStep < 5 && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setImportStep((s) => s - 1)}
                  disabled={importLoading}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t("settings.import.back", { defaultValue: "Voltar" })}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {importStep === 1 && (
                <Button
                  type="button"
                  onClick={handleImportContinueFromFile}
                  disabled={!importFile || importLoading}
                >
                  {importLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="mr-2 h-4 w-4" />
                  )}
                  {t("settings.import.continue", { defaultValue: "Continuar" })}
                </Button>
              )}
              {importStep === 2 && (
                <Button type="button" onClick={handleImportReview} disabled={importLoading}>
                  {importLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Users className="mr-2 h-4 w-4" />
                  )}
                  {t("settings.import.review", { defaultValue: "Revisar" })}
                </Button>
              )}
              {importStep === 3 && (
                <Button
                  type="button"
                  onClick={() => setImportStep(4)}
                  disabled={
                    importLoading ||
                    (importDryRun !== null &&
                      importDryRun.report.warnings.length > 0 &&
                      !importDupConfirmed)
                  }
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  {t("settings.import.continue", { defaultValue: "Continuar" })}
                </Button>
              )}
              {importStep === 4 && (
                <Button
                  type="button"
                  onClick={handleImportCommit}
                  disabled={importLoading}
                  className="bg-violet-600 text-white hover:bg-violet-700"
                >
                  {importLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                  )}
                  {t("settings.import.confirm", { defaultValue: "Importar agora" })}
                </Button>
              )}
              {importStep === 5 && (
                <Button
                  type="button"
                  onClick={() => {
                    setImportOpen(false);
                    resetImportWizard();
                  }}
                  className="bg-violet-600 text-white hover:bg-violet-700"
                >
                  {t("settings.import.close", { defaultValue: "Fechar" })}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          if (!deletingAccount) setDeleteDialogOpen(open);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              {t("settings.deleteAccount.dialogTitle", {
                defaultValue: "Excluir conta permanentemente?",
              })}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                {t("settings.deleteAccount.dialogWarning", {
                  defaultValue:
                    "Esta ação NÃO pode ser desfeita. Todos os dados da empresa serão apagados, incluindo clientes, projetos, faturas, contratos, horas, despesas, equipe e o login de todos os usuários.",
                })}
              </span>
              <span className="block font-medium">
                {t("settings.deleteAccount.dialogConfirmHint", {
                  defaultValue: "Para confirmar, digite EXCLUIR no campo abaixo.",
                })}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={t("settings.deleteAccount.confirmKeyword", { defaultValue: "EXCLUIR" })}
            disabled={deletingAccount}
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingAccount}>
              {t("common.cancel", { defaultValue: "Cancelar" })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAccount();
              }}
              disabled={
                deleteConfirmText !==
                  t("settings.deleteAccount.confirmKeyword", { defaultValue: "EXCLUIR" }) ||
                deletingAccount
              }
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingAccount ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {deletingAccount
                ? t("settings.deleteAccount.deleting", {
                    defaultValue: "Excluindo...",
                  })
                : t("settings.deleteAccount.confirm", {
                    defaultValue: "Sim, excluir tudo",
                  })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
